import { Policy, SubjectLookup } from '@domain/access-control/types/policy';
import { useCallback } from 'react';

export interface AccessDecisions<Subject> {
  /** Judges a policy against the subject. The result is valid only after the subject loads. */
  decide: (policy: Policy<Subject>) => boolean;
  error?: Error;
  loading: boolean;
  retry: () => void;
}

/**
 * Generic policy decision point. It judges policies against a subject that an
 * experience-level lookup assembled (for example, useSalesPortalAccess). It
 * does not know where subjects come from. Enforcement points (navigators,
 * screens, nav items) call `decide` with a domain-owned policy. They do not
 * read the subject's attributes.
 */
export const useAccessDecisions = <Subject>({
  error,
  loading,
  retry,
  subject,
}: SubjectLookup<Subject>): AccessDecisions<Subject> => {
  const decide = useCallback(
    (policy: Policy<Subject>) => policy(subject),
    [subject],
  );

  return { decide, error, loading, retry };
};
